import { useRef, useEffect, useCallback, useState } from "react";

interface UsePillMeasurementProps {
  containerRef: React.RefObject<HTMLDivElement | null>;
  pillsContainerRef: React.RefObject<HTMLDivElement | null>;
  totalItems: number;
  hasContext: boolean;
}

export const usePillMeasurement = ({
  containerRef,
  pillsContainerRef,
  totalItems,
  hasContext,
}: UsePillMeasurementProps) => {
  const [visibleCount, setVisibleCount] = useState<number>(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const prevVisibleCountRef = useRef<number>(0);
  const prevTotalItemsRef = useRef<number>(0);

  // Get all pills from measurement container (contains all pills for accurate measurement)
  const getAllPills = useCallback((): NodeListOf<Element> | null => {
    if (!pillsContainerRef.current) return null;
    const measurementContainer = pillsContainerRef.current.querySelector(
      "[data-measurement-container]"
    );
    return measurementContainer
      ? measurementContainer.querySelectorAll("[data-pill-item]")
      : null;
  }, [pillsContainerRef]);

  // Calculate how many pills can fit
  const calculateVisibleCount = useCallback(() => {
    if (!containerRef.current || !pillsContainerRef.current) {
      return;
    }
    const pillsContainerRect =
      pillsContainerRef.current.getBoundingClientRect();
    const availableWidth = pillsContainerRect.width;

    if (availableWidth <= 0) {
      setVisibleCount(0);
      return;
    }

    const allPills = getAllPills();
    if (!allPills || allPills.length === 0) {
      setVisibleCount(0);
      return;
    }

    let usedWidth = 0;
    let count = 0;
    const gap = 8;
    const overflowBadgeWidth = 60;

    for (let i = 0; i < allPills.length; i++) {
      const pill = allPills[i] as HTMLElement;
      const pillWidth = pill.offsetWidth;
      const nextWidth = usedWidth + pillWidth + (count > 0 ? gap : 0);
      const isLastItem = i === allPills.length - 1;
      const totalNeeded = nextWidth + (!isLastItem ? overflowBadgeWidth : 0);

      if (totalNeeded <= availableWidth) {
        usedWidth = nextWidth;
        count++;
      } else {
        break;
      }
    }

    const newCount = Math.max(0, count);

    const countDifference = Math.abs(newCount - prevVisibleCountRef.current);
    const isSignificantChange =
      countDifference > 2 ||
      prevVisibleCountRef.current === 0 ||
      newCount === 0 ||
      (countDifference === 1 && newCount > 0 && newCount < totalItems);

    if (isSignificantChange) {
      setVisibleCount(newCount);
      prevVisibleCountRef.current = newCount;
    }
  }, [containerRef, pillsContainerRef, totalItems, getAllPills]);

  // Debounced measurement
  const measure = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      // Use requestAnimationFrame to ensure DOM has settled
      requestAnimationFrame(() => {
        calculateVisibleCount();
      });
    }, 150);
  }, [calculateVisibleCount]);

  useEffect(() => {
    if (!hasContext || totalItems === 0) {
      setVisibleCount(0);
      prevVisibleCountRef.current = 0;
      prevTotalItemsRef.current = 0;
      return;
    }

    // Reset on significant item count changes
    const totalItemsChanged = totalItems !== prevTotalItemsRef.current;
    if (
      totalItemsChanged &&
      (prevTotalItemsRef.current === 0 || totalItems === 0)
    ) {
      prevVisibleCountRef.current = 0;
    }
    prevTotalItemsRef.current = totalItems;

    // Initial measurement with slight delay for DOM to settle
    const timeoutId = setTimeout(() => {
      measure();
    }, 250);

    // Observe container resizes
    const resizeObserver = new ResizeObserver(() => {
      measure();
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [hasContext, totalItems, measure, containerRef]);

  // Use previous count if current is 0 to prevent jumping
  const effectiveVisibleCount =
    visibleCount > 0 && visibleCount < totalItems
      ? visibleCount
      : prevVisibleCountRef.current > 0 &&
          prevVisibleCountRef.current < totalItems
        ? prevVisibleCountRef.current
        : totalItems;

  return {
    visibleCount: effectiveVisibleCount,
    recalculate: measure,
  };
};
